/**
 * Handles user actions inside the AI Chat workbench panels. It deliberately
 * delegates persistence to the supplied facades and keeps bootstrap free of
 * artifact/canvas/memory event branching.
 */
export function createWorkbenchActionController({
  artifactList,
  canvasEmpty,
  memoryList,
  textarea,
  sessionStore,
  artifactFacade,
  runFacade,
  memoryFacade,
  artifactStore,
  canvasStore,
  workbenchController,
  windowRef = window,
}) {
  let bound = false;

  function getCurrentSessionId() {
    return sessionStore.getSessionState().currentSession?.id;
  }

  function handleArtifactClick(event) {
    const sessionId = getCurrentSessionId();
    if (event.target.closest("[data-action='retry-load-artifacts']")) {
      if (sessionId) artifactFacade.loadArtifacts(sessionId);
      return;
    }
    if (event.target.closest("[data-action='retry-load-runs']")) {
      if (sessionId) runFacade.loadRuns(sessionId);
      return;
    }
    const addButton = event.target.closest('[data-artifact-add]');
    if (addButton) {
      const artifact = artifactStore.getArtifactState().artifacts.find((item) => String(item.memberIds?.join(',') || item.id) === String(addButton.dataset.artifactAdd));
      if (!artifact) return;
      canvasStore.addArtifact(artifact);
      workbenchController.switchWorkbench('canvas');
      return;
    }
    const lifecycleButton = event.target.closest('[data-artifact-lifecycle]');
    if (!lifecycleButton) return;
    const action = lifecycleButton.dataset.artifactLifecycle;
    const artifactId = lifecycleButton.dataset.artifactId;
    if (!artifactId || !action || !sessionId) return;
    if (action === 'revoke' && !windowRef.confirm('撤销后，这张素材不会再作为 Agent 的参考上下文。确认撤销吗？')) return;
    lifecycleButton.disabled = true;
    artifactFacade.updateArtifactLifecycle({ sessionId, artifactId, action })
      .then(() => {
        if (action === 'revoke' || action === 'archive') String(artifactId).split(',').forEach((id) => canvasStore.removeArtifact(id));
      })
      .catch((error) => {
        lifecycleButton.disabled = false;
        windowRef.alert(error?.message || '资产状态更新失败');
      });
  }

  function handleCanvasClick(event) {
    const removeButton = event.target.closest('[data-canvas-remove]');
    if (removeButton) return canvasStore.removeArtifact(removeButton.dataset.canvasRemove);
    if (event.target.closest('[data-canvas-clear]')) return canvasStore.clearCanvas();
    if (event.target.closest('[data-canvas-use]')) textarea?.focus();
  }

  function handleMemoryClick(event) {
    const button = event.target.closest('[data-memory-action]');
    if (!button) return;
    const action = button.dataset.memoryAction;
    if (action === 'reload') return void memoryFacade.loadMemoryItems();
    const memoryId = button.dataset.memoryId;
    if (!memoryId) return;
    if ((action === 'expired' || action === 'deleted') && !windowRef.confirm(action === 'expired' ? '确认将这条记忆设为过期？之后不会自动复用。' : '确认删除这条记忆？')) return;
    button.disabled = true;
    memoryFacade.updateMemoryStatus({ memoryId, status: action }).catch((error) => {
      button.disabled = false;
      windowRef.alert(error?.message || '记忆状态更新失败');
    });
  }

  function bind() {
    if (bound) return;
    artifactList?.addEventListener('click', handleArtifactClick);
    canvasEmpty?.addEventListener('click', handleCanvasClick);
    memoryList?.addEventListener('click', handleMemoryClick);
    bound = true;
  }

  function destroy() {
    if (!bound) return;
    artifactList?.removeEventListener('click', handleArtifactClick);
    canvasEmpty?.removeEventListener('click', handleCanvasClick);
    memoryList?.removeEventListener('click', handleMemoryClick);
    bound = false;
  }

  return { bind, destroy };
}
